import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Instagram, Youtube, Twitter, Globe } from "lucide-react";
import { api } from "../api";
import { SEO } from "../components/SEO";
import { resolveMediaUrl, optimizeCloudinaryUrl } from "../components/PageContent";

// CrewDetail's resolveUrl preserves the legacy /api/ prefix behaviour AND
// applies Cloudinary optimization (q_auto, f_auto) for any res.cloudinary.com URL.
const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return optimizeCloudinaryUrl(url);
};

const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v)$/i.test(url || "");

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram, urlBuilder: (v) => (v.startsWith("http") ? v : `https://instagram.com/${v.replace(/^@/, "")}`) },
  { key: "twitter", label: "Twitter", Icon: Twitter, urlBuilder: (v) => (v.startsWith("http") ? v : `https://twitter.com/${v.replace(/^@/, "")}`) },
  { key: "youtube", label: "YouTube", Icon: Youtube, urlBuilder: (v) => (v.startsWith("http") ? v : `https://youtube.com/${v.replace(/^@/, "")}`) },
  { key: "tiktok", label: "TikTok", Icon: Globe, urlBuilder: (v) => (v.startsWith("http") ? v : `https://tiktok.com/@${v.replace(/^@/, "")}`) },
  { key: "website", label: "Website", Icon: Globe, urlBuilder: (v) => (v.startsWith("http") ? v : `https://${v}`) },
];

const parseExtraSections = (text) => {
  if (!text) return [];
  return text
    .split(/\r?\n---\r?\n/g)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => {
      const [first, ...rest] = block.split(/\r?\n/);
      return { heading: first.trim(), body: rest.join("\n").trim() };
    });
};

const STATUS_LABEL = {
  crew: "Sworn In",
  prospects: "On Trial",
  "hang-arounds": "Loitering",
};

export default function CrewDetail() {
  const { slug } = useParams();
  const [member, setMember] = useState(null);
  const [error, setError] = useState(false);
  const [products, setProducts] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    let active = true;
    setMember(null);
    setError(false);
    api
      .get(`/crew/${slug}`)
      .then((r) => {
        if (active) setMember(r.data);
      })
      .catch(() => {
        if (active) setError(true);
      });
    api.get("/shop/products?limit=48").then((r) => active && setProducts(r.data?.items || [])).catch(() => {});
    api.get("/stories").then((r) => active && setStories(r.data || [])).catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  const featuredDecks = useMemo(() => {
    if (!member) return [];
    const slugs = new Set(member.featured_deck_slugs || []);
    return products.filter((p) => slugs.has(p.slug));
  }, [member, products]);

  const featuredProducts = useMemo(() => {
    if (!member) return [];
    const slugs = new Set(member.featured_product_slugs || []);
    return products.filter((p) => slugs.has(p.slug));
  }, [member, products]);

  const relatedArticles = useMemo(() => {
    if (!member) return [];
    const slugs = new Set(member.related_article_slugs || []);
    return stories.filter((s) => slugs.has(s.slug));
  }, [member, stories]);

  const extraSections = useMemo(() => parseExtraSections(member?.extra_sections), [member]);

  if (error) {
    return (
      <div className="min-h-screen bg-bone py-24 text-center" data-testid="crew-detail-error">
        <SEO title="Crew member not found" path={`/crew/${slug}`} noindex />
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-terra mb-3">✦ No record</div>
        <h1 className="font-western text-5xl mb-4">Vanished into the desert.</h1>
        <p className="font-body text-base mb-8">We have no file on &ldquo;{slug}&rdquo;. Maybe they paid their bounty.</p>
        <Link
          to="/crew"
          className="inline-flex items-center gap-2 border-2 border-asphalt px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] shadow-hard hover:bg-asphalt hover:text-bone transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the rolls
        </Link>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-bone py-24 text-center font-mono text-xs uppercase tracking-[0.3em]" data-testid="crew-detail-loading">
        Pulling the file…
      </div>
    );
  }

  const heroUrl = resolveUrl(member.photo);
  const photos = member.photos || [];
  const videos = member.videos || [];

  return (
    <div className="bg-card-light min-h-screen" data-testid="crew-detail-page">
      <SEO
        title={`${member.alias ? `"${member.alias}" ` : ""}${member.name}`}
        description={member.bio?.slice(0, 160) || `${member.name} — ${member.role}. Bounty: ${member.bounty || "negotiable"}.`}
        image={resolveUrl(member.photo)}
        type="profile"
        path={`/crew/${member.slug}`}
      />
      {/* Hero */}
      <section className="relative bg-asphalt text-bone overflow-hidden" data-testid="crew-detail-hero">
        {heroUrl && (
          <>
            <img
              src={heroUrl}
              alt={member.name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "grayscale(100%) contrast(1.15) brightness(0.55)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/60 to-asphalt/30" />
          </>
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 min-h-[60vh] flex flex-col justify-end">
          <Link
            to="/crew"
            data-testid="crew-detail-back"
            className="inline-flex items-center gap-2 self-start mb-8 border-2 border-bone px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-bone hover:text-asphalt transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to the rolls
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] text-terra mb-3">
            ✦ {STATUS_LABEL[member.status] || "On the Rolls"} · {member.role}
          </div>
          <div className="font-western text-3xl sm:text-5xl text-bone/90 mb-1">
            &ldquo;{member.alias}&rdquo;
          </div>
          <h1 className="font-display text-5xl sm:text-7xl uppercase tracking-tighter leading-none" data-testid="crew-detail-name">
            {member.name}
          </h1>
          {member.hometown && (
            <div className="font-mono text-xs uppercase tracking-[0.3em] mt-4 text-bone/80">
              Hails from {member.hometown}
            </div>
          )}
        </div>
        <div className="tape-divider" />
      </section>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        {/* Side card */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="border-4 border-asphalt bg-bone shadow-hard" data-testid="crew-detail-rapsheet">
            <div className="border-b-2 border-asphalt p-4 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.4em]">Rap Sheet</div>
              <div className="font-western text-2xl mt-1">Wanted</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink">Bounty</div>
                <div className="font-display text-2xl uppercase tracking-tight">{member.bounty || "Negotiable"}</div>
              </div>
              {(member.crimes || []).length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink mb-1">Known Crimes</div>
                  <ul className="font-body text-sm space-y-1 list-disc list-inside">
                    {member.crimes.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Socials */}
          {SOCIAL_PLATFORMS.some((p) => member[p.key]) && (
            <div className="border-2 border-asphalt bg-bone p-5 shadow-hard" data-testid="crew-detail-socials">
              <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-ink mb-3">Find &lsquo;em</div>
              <div className="flex gap-3 flex-wrap">
                {SOCIAL_PLATFORMS.map(({ key, label, Icon, urlBuilder }) => {
                  const v = member[key];
                  if (!v) return null;
                  return (
                    <a
                      key={key}
                      href={urlBuilder(v)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`crew-social-${key}`}
                      className="inline-flex items-center gap-2 border-2 border-asphalt px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-asphalt hover:text-bone transition-colors"
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="lg:col-span-8 space-y-12">
          {member.bio && (
            <section data-testid="crew-detail-bio">
              <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ Full Biography</div>
              <p className="font-body text-lg leading-relaxed text-ink whitespace-pre-line">{member.bio}</p>
            </section>
          )}

          {extraSections.map((s, i) => (
            <section key={`${s.heading}-${i}`} data-testid={`crew-extra-section-${i}`}>
              <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ {s.heading}</div>
              <p className="font-body text-base leading-relaxed text-ink whitespace-pre-line">{s.body}</p>
            </section>
          ))}

          {photos.length > 0 && (
            <section data-testid="crew-detail-gallery">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-1">✦ Photo Gallery</div>
                  <h2 className="font-western text-3xl sm:text-4xl">Documented</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {photos.map((p, i) => (
                  <a
                    key={p}
                    href={resolveUrl(p)}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square overflow-hidden border-2 border-asphalt bg-asphalt hover:-translate-y-1 hover:-translate-x-1 transition-transform shadow-hard"
                    data-testid={`crew-gallery-${i}`}
                  >
                    <img src={resolveUrl(p)} alt="" className="w-full h-full object-cover ink-stamp" />
                  </a>
                ))}
              </div>
            </section>
          )}

          {videos.length > 0 && (
            <section data-testid="crew-detail-videos">
              <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ Video Gallery</div>
              <h2 className="font-western text-3xl sm:text-4xl mb-4">Moving Evidence</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {videos.map((v, i) => {
                  const url = resolveUrl(v);
                  if (isVideoUrl(v) || v.startsWith("/api/")) {
                    return (
                      <video
                        key={v}
                        src={url}
                        controls
                        playsInline
                        className="w-full aspect-video border-2 border-asphalt bg-asphalt shadow-hard"
                        data-testid={`crew-video-${i}`}
                      />
                    );
                  }
                  return (
                    <div key={v} className="border-2 border-asphalt bg-asphalt aspect-video flex items-center justify-center" data-testid={`crew-video-${i}`}>
                      <a href={url} target="_blank" rel="noreferrer" className="font-mono text-xs uppercase tracking-[0.25em] text-bone underline">
                        View video →
                      </a>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {featuredDecks.length > 0 && (
            <ProductRow
              title="Featured Decks"
              eyebrow="Ride Their Wood"
              items={featuredDecks}
              testid="crew-featured-decks"
            />
          )}

          {featuredProducts.length > 0 && (
            <ProductRow
              title="Featured Products"
              eyebrow="Goods of Questionable Necessity"
              items={featuredProducts}
              testid="crew-featured-products"
            />
          )}

          {relatedArticles.length > 0 && (
            <section data-testid="crew-related-articles">
              <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ Related Articles</div>
              <h2 className="font-western text-3xl sm:text-4xl mb-4">From The Files</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {relatedArticles.map((s, i) => (
                  <Link
                    to={`/stories/${s.slug}`}
                    key={s.id}
                    data-testid={`crew-article-${i}`}
                    className="block border-2 border-asphalt bg-bone shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
                  >
                    {s.cover_image && (
                      <div className="aspect-[16/9] overflow-hidden border-b-2 border-asphalt bg-asphalt">
                        <img src={resolveUrl(s.cover_image)} alt={s.title} className="w-full h-full object-cover ink-stamp" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra mb-1">✦ Dispatch</div>
                      <div className="font-display text-xl uppercase tracking-tight">{s.title}</div>
                      {s.excerpt && (
                        <p className="font-body text-sm text-ink mt-2 line-clamp-2">{s.excerpt}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

const ProductRow = ({ title, eyebrow, items, testid }) => (
  <section data-testid={testid}>
    <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-2">✦ {eyebrow}</div>
    <h2 className="font-western text-3xl sm:text-4xl mb-4">{title}</h2>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {items.map((p, i) => (
        <Link
          to={`/shop/${p.slug}`}
          key={p.id}
          data-testid={`${testid}-${i}`}
          className="block border-2 border-asphalt bg-bone shadow-hard hover:-translate-y-1 hover:-translate-x-1 hover:shadow-hard-lg transition-all"
        >
          {p.image && (
            <div className="aspect-square overflow-hidden border-b-2 border-asphalt bg-bone">
              <img src={resolveUrl(p.image)} alt={p.name} className="w-full h-full object-cover ink-stamp" />
            </div>
          )}
          <div className="p-3 text-center">
            <div className="font-display text-base uppercase tracking-tight truncate">{p.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink mt-1">${Number(p.price).toFixed(2)}</div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);
